import { useState, useEffect, useCallback } from 'react';
import { Megaphone, X, Bug, Lightbulb, Frown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Feedback button for the Steuerberater portal.
 * Uses advisor_token and posts to /api/advisor/feedback.
 */
const AdvisorFeedbackButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // P3 z-index-Fix (Feb 2026): dimmen, wenn Closure-Wizard offen ist —
  // sonst überlagert der z-50 Feedback-Button die Wizard-Modale.
  const [dimmed, setDimmed] = useState(false);
  useEffect(() => {
    const onOpen = () => setDimmed(true);
    const onClose = () => setDimmed(false);
    window.addEventListener('ivoliq:wizard-open', onOpen);
    window.addEventListener('ivoliq:wizard-close', onClose);
    return () => {
      window.removeEventListener('ivoliq:wizard-open', onOpen);
      window.removeEventListener('ivoliq:wizard-close', onClose);
    };
  }, []);

  const feedbackTypes = [
    { id: 'positive', icon: Megaphone, title: 'Lob', color: 'text-emerald-600 bg-emerald-50 border-emerald-200 hover:bg-emerald-100' },
    { id: 'negative', icon: Frown, title: 'Problem', color: 'text-red-600 bg-red-50 border-red-200 hover:bg-red-100' },
    { id: 'idea', icon: Lightbulb, title: 'Idee', color: 'text-amber-600 bg-amber-50 border-amber-200 hover:bg-amber-100' },
  ];

  const resetForm = useCallback(() => {
    setSelectedType(null);
    setDescription('');
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    resetForm();
  }, [resetForm]);

  const handleSubmit = async () => {
    if (!selectedType || description.trim().length < 10) {
      toast.error('Bitte Typ wählen und mindestens 10 Zeichen eingeben.');
      return;
    }
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('advisor_token');
      const res = await axios.post(`${API}/advisor/feedback`, {
        feedback_type: selectedType,
        description: description.trim(),
        category: 'other',
        page_url: window.location.href,
        browser_info: navigator.userAgent.substring(0, 200),
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.success) {
        toast.success('Feedback gesendet! Vielen Dank.');
        handleClose();
      } else {
        toast.error(res.data.message || 'Feedback konnte nicht gesendet werden.');
      }
    } catch (err) {
      toast.error('Fehler beim Senden.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render if advisor not logged in (cookie-auth flag)
  if (!localStorage.getItem('advisor_authed')) return null;

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-12 h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105 ${dimmed ? 'opacity-20 pointer-events-none' : ''}`}
        title="Feedback geben"
        data-testid="advisor-feedback-btn"
      >
        <Megaphone className="w-5 h-5" />
      </button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Feedback — Steuerberater-Portal</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Type selection */}
            <div className="grid grid-cols-3 gap-2">
              {feedbackTypes.map(({ id, icon: Icon, title, color }) => (
                <button
                  key={id}
                  onClick={() => setSelectedType(id)}
                  className={`flex flex-col items-center gap-1.5 p-3 border rounded-lg transition-all ${
                    selectedType === id ? color + ' ring-2 ring-offset-1' : 'border-zinc-200 hover:bg-zinc-50'
                  }`}
                  data-testid={`feedback-type-${id}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-xs font-medium">{title}</span>
                </button>
              ))}
            </div>

            {/* Description */}
            {selectedType && (
              <div>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Beschreiben Sie Ihr Feedback... (min. 10 Zeichen)"
                  className="min-h-[100px] resize-none"
                  data-testid="feedback-description"
                />
                <div className="text-xs text-slate-400 mt-1 text-right">{description.length}/500</div>
              </div>
            )}

            {/* Submit */}
            {selectedType && (
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={handleClose} data-testid="feedback-cancel">Abbrechen</Button>
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || description.trim().length < 10}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="feedback-submit"
                >
                  {isSubmitting ? 'Senden...' : 'Absenden'}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdvisorFeedbackButton;
